import { useState } from 'react'
import { useOperations, useCancelOperation } from '@/api/operations'
import { useFleets } from '@/api/devices'
import { RequirePermission } from '@/components/RequirePermission'
import type { OperationStatus, OperationType } from '@/types'

interface Props {
  tenantId: string | null
}

const STATUS_LABELS: Record<OperationStatus, string> = {
  pending: 'Pending',
  running: 'Running',
  succeeded: 'Succeeded',
  failed: 'Failed',
  cancelled: 'Cancelled',
}

const TYPE_LABELS: Record<OperationType, string> = {
  ota_rollout: 'OTA Rollout',
  config_push: 'Config Push',
  playbook: 'Playbook',
}

function duration(createdAt: string, completedAt?: string): string {
  const start = new Date(createdAt).getTime()
  const end = completedAt ? new Date(completedAt).getTime() : Date.now()
  const secs = Math.floor((end - start) / 1000)
  if (secs < 60) return `${secs}s`
  if (secs < 3600) return `${Math.floor(secs / 60)}m ${secs % 60}s`
  return `${Math.floor(secs / 3600)}h ${Math.floor((secs % 3600) / 60)}m`
}

export function Operations({ tenantId }: Props) {
  const [typeFilter, setTypeFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')

  const { data, isLoading, error } = useOperations(tenantId)
  const { data: fleetsData } = useFleets(tenantId)
  const cancelOp = useCancelOperation(tenantId ?? '')

  const fleetMap = Object.fromEntries(
    (fleetsData?.data ?? []).map(f => [f.id, f.name]),
  )

  const operations = (data?.data ?? []).filter(op => {
    if (typeFilter && op.type !== typeFilter) return false
    if (statusFilter && op.status !== statusFilter) return false
    return true
  })

  if (!tenantId) {
    return <div className="empty-state">Select a tenant to view operations.</div>
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Operations</h1>
          <p className="page-subtitle">{data?.total ?? 0} total</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <select
            className="btn btn-ghost"
            value={typeFilter}
            onChange={e => setTypeFilter(e.target.value)}
            style={{ cursor: 'pointer' }}
          >
            <option value="">All types</option>
            {(Object.keys(TYPE_LABELS) as OperationType[]).map(t => (
              <option key={t} value={t}>{TYPE_LABELS[t]}</option>
            ))}
          </select>

          <select
            className="btn btn-ghost"
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            style={{ cursor: 'pointer' }}
          >
            <option value="">All statuses</option>
            {(Object.keys(STATUS_LABELS) as OperationStatus[]).map(s => (
              <option key={s} value={s}>{STATUS_LABELS[s]}</option>
            ))}
          </select>
        </div>
      </div>

      {isLoading && <div className="empty-state">Loading…</div>}
      {error && (
        <div className="empty-state" style={{ color: 'var(--color-danger)' }}>
          Error loading operations.
        </div>
      )}

      {!isLoading && operations.length === 0 && (
        <div className="empty-state">
          Nessuna operazione.{' '}
          <span style={{ color: 'var(--color-text-dim)', fontSize: 12 }}>
            Le operazioni vengono create per OTA rollout e config push manuali.
            I job degli Scheduled Tasks sono visibili nella pagina <strong>Events</strong>.
          </span>
        </div>
      )}

      {operations.length > 0 && (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>ID</th>
                <th>Type</th>
                <th>Status</th>
                <th>Fleet</th>
                <th>Duration</th>
                <th>Created</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {operations.map(op => (
                <tr key={op.id}>
                  <td
                    style={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: 11,
                      color: 'var(--color-text-muted)',
                    }}
                  >
                    {op.id.slice(0, 8)}…
                  </td>
                  <td>
                    <span
                      style={{
                        background: 'var(--color-surface-2)',
                        padding: '2px 8px',
                        borderRadius: 'var(--radius-sm)',
                        fontSize: 12,
                      }}
                    >
                      {TYPE_LABELS[op.type]}
                    </span>
                  </td>
                  <td>
                    <span className={`badge badge-${op.status}`}>
                      {STATUS_LABELS[op.status]}
                    </span>
                  </td>
                  <td style={{ color: 'var(--color-text-muted)', fontSize: 13 }}>
                    {op.fleet_id ? (fleetMap[op.fleet_id] ?? op.fleet_id.slice(0, 8) + '…') : '—'}
                  </td>
                  <td style={{ color: 'var(--color-text-muted)', fontSize: 12 }}>
                    {duration(op.created_at, op.completed_at)}
                    {(op.status === 'pending' || op.status === 'running') && (
                      <span
                        style={{
                          marginLeft: 6,
                          display: 'inline-block',
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          background: 'var(--color-accent)',
                          animation: 'pulse 1.5s infinite',
                        }}
                      />
                    )}
                  </td>
                  <td style={{ color: 'var(--color-text-muted)', fontSize: 12 }}>
                    {new Date(op.created_at).toLocaleString()}
                  </td>
                  <td>
                    {(op.status === 'pending' || op.status === 'running') && (
                      <RequirePermission perm="operations:execute">
                        <button
                          className="btn btn-ghost"
                          style={{ padding: '4px 10px', fontSize: 12 }}
                          onClick={() => cancelOp.mutate(op.id)}
                          disabled={cancelOp.isPending}
                        >
                          Cancel
                        </button>
                      </RequirePermission>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
